import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  Input,
  MenuProps,
  Row,
  Select,
  Space,
  Table,
  Tooltip
} from 'antd';
import { CrudFilters, HttpError, useNavigation } from '@refinedev/core';
import { EditButton, TextField, useTable } from '@refinedev/antd';
import { ICourseCertificate } from 'interfaces/course_certificate';
import { ListLayout } from '../../../components/layout/layout/ListLayout';
import { PlusOutlined } from '@ant-design/icons';
import { formatDate } from 'services/date';
import { getCourseCertificateUrl } from 'requests/certificates';
import { useCallback, useState } from 'react';
import { useCertificateReport } from 'hooks/useCertificateReport';

interface IFilter {
  by_user_email: string;
  by_course_name: string;
  by_created_at: dayjs.Dayjs;
  by_status: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'issued', label: 'Emitidos' },
  { value: 'pending', label: 'Pendentes' }
];

const REPORT_STORAGE_KEY = 'bulk_course_certificates_report_id';

type ReportKind = 'zip' | 'merged_pdf';

const REPORT_ENDPOINTS: Record<ReportKind, string> = {
  zip: 'admin/bulk_course_certificates/report',
  merged_pdf: 'admin/merged_course_certificates/report'
};

// O lote e montado a partir do filtro aplicado, entao baixar sem filtro
// significaria imprimir a base inteira.
const MAX_CERTIFICATES = 300;

const DOWNLOAD_OPTIONS: { key: ReportKind; label: string }[] = [
  { key: 'merged_pdf', label: 'PDF único para impressão' },
  { key: 'zip', label: 'ZIP com um arquivo por aluno' }
];

const REPORT_SUCCESS_MESSAGES: Record<ReportKind, string> = {
  zip: 'Os certificados estão sendo gerados, e você será notificado quando estiverem prontos para download.',
  merged_pdf:
    'O PDF único está sendo gerado, e você será notificado quando estiver pronto para download.'
};

const buildReportFilters = (formValues?: Partial<IFilter>) => {
  const filters: Record<string, string> = {};

  if (formValues?.by_user_email) {
    filters.by_user_email = formValues.by_user_email;
  }

  if (formValues?.by_course_name) {
    filters.by_course_name = formValues.by_course_name;
  }

  if (formValues?.by_created_at) {
    filters.by_created_at = formValues.by_created_at.format('YYYY-MM-DD');
  }

  if (formValues?.by_status) {
    filters.by_status = formValues.by_status;
  }

  return filters;
};

interface ActionsProps {
  certificateId: string;
}

const DownloadButton = ({ certificateId }: ActionsProps) => {
  const [loading, setLoading] = useState(false);

  const downloadCertificate = async () => {
    setLoading(true);

    try {
      const { url } = await getCourseCertificateUrl({
        certificateId: certificateId
      });

      window.open(url, '_blank');
    } catch {}
    setLoading(false);
  };

  return (
    <div className="flex gap-2">
      <Space>
        <Button
          size="small"
          onClick={downloadCertificate}
          loading={loading}
          icon={<Icons.DownloadOutlined />}
        />
      </Space>
    </div>
  );
};

interface DownloadMenuProps {
  hasFilter: boolean;
  onlyPending: boolean;
  total?: number;
  reportId: string;
  runningKind: string;
  onSelect: (kind: ReportKind) => void;
}

const downloadBlockedReason = (
  hasFilter: boolean,
  onlyPending: boolean,
  running: boolean,
  count: number
) => {
  if (running) {
    return 'Aguarde o download em andamento terminar';
  }

  if (!hasFilter) {
    return 'Aplique um filtro para baixar em lote';
  }

  // Pendente nao tem documento: o PDF e recusado pela API e o ZIP sairia vazio.
  if (onlyPending) {
    return 'Certificados pendentes ainda não têm arquivo para baixar';
  }

  if (count === 0) {
    return 'Nenhum certificado no resultado';
  }

  if (count > MAX_CERTIFICATES) {
    return `O resultado tem ${count} certificados; o máximo é ${MAX_CERTIFICATES}`;
  }

  return '';
};

const DownloadMenu = ({
  hasFilter,
  onlyPending,
  total,
  reportId,
  runningKind,
  onSelect
}: DownloadMenuProps) => {
  const running = !!reportId;
  const blocked = downloadBlockedReason(
    hasFilter,
    onlyPending,
    running,
    total ?? 0
  );

  const items: MenuProps['items'] = DOWNLOAD_OPTIONS.map(option => ({
    key: option.key,
    label: option.label
  }));

  const runningLabel = DOWNLOAD_OPTIONS.find(
    option => option.key === runningKind
  )?.label;

  return (
    // O Dropdown repassa `disabled` ao Button, e botao desabilitado nao dispara
    // evento de mouse: sem o span por fora e o pointer-events por dentro, o
    // Tooltip que explica o bloqueio nunca aparece.
    <Tooltip title={blocked}>
      <span className={blocked ? 'cursor-not-allowed' : undefined}>
        <Dropdown
          menu={{ items, onClick: event => onSelect(event.key as ReportKind) }}
          disabled={!!blocked}
        >
          <Button
            icon={<Icons.DownloadOutlined />}
            loading={running}
            style={blocked ? { pointerEvents: 'none' } : undefined}
          >
            <Space>
              {running ? `Gerando ${runningLabel ?? 'arquivo'}...` : 'Baixar'}
              {!running && <Icons.DownOutlined />}
            </Space>
          </Button>
        </Dropdown>
      </span>
    </Tooltip>
  );
};

export const CertificateList = () => {
  const { reportId, runningKind, generate } =
    useCertificateReport(REPORT_STORAGE_KEY);

  // O relatório precisa sair com o mesmo filtro que produziu o total exibido,
  // e não com o que estiver digitado mas ainda não aplicado.
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(
    {}
  );

  const { tableProps, searchFormProps } = useTable<
    ICourseCertificate,
    HttpError,
    IFilter
  >({
    onSearch: params => {
      setAppliedFilters(buildReportFilters(params));

      const filters: CrudFilters = [];
      const { by_user_email, by_course_name, by_created_at, by_status } =
        params;

      filters.push(
        {
          field: 'filter[by_user_email]',
          operator: 'eq',
          value: by_user_email
        },
        {
          field: 'filter[by_course_name]',
          operator: 'eq',
          value: by_course_name
        },
        {
          field: 'filter[by_created_at]',
          operator: 'eq',
          value: by_created_at
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: by_status
        }
      );

      return filters;
    },
    sorters: {
      mode: 'server'
    }
  });

  const router = useNavigation();

  const handleGenerateCertificatesReport = useCallback(
    (kind: ReportKind) =>
      generate({
        endpoint: REPORT_ENDPOINTS[kind],
        kind,
        filters: appliedFilters,
        successMessage: REPORT_SUCCESS_MESSAGES[kind]
      }),
    [generate, appliedFilters]
  );

  const items: MenuProps['items'] = [
    {
      label: 'Novo certificado',
      key: 'create'
    },
    {
      label: 'Adicionar certificados em lote',
      key: 'bulk_certificates'
    }
  ];

  const handleMenuClick: MenuProps['onClick'] = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      switch (e.key) {
        case 'create': {
          router.create('course_certificates');
          break;
        }

        case 'bulk_certificates': {
          router.create('bulk_course_certificates');
          break;
        }
      }
    },
    [router]
  );

  const menuProps = {
    items,
    onClick: handleMenuClick
  };

  const pagination = tableProps.pagination;
  const total =
    pagination && typeof pagination === 'object' ? pagination.total : undefined;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <ListLayout
          title="Certificados de Cursos Livres"
          headerActions={
            <Flex>
              <Dropdown menu={menuProps}>
                <Button type="primary">
                  <Space>
                    <PlusOutlined />
                    Ações
                  </Space>
                </Button>
              </Dropdown>
            </Flex>
          }
        >
          <Card className="filter-form-card p-2 mb-3" size="small">
            <Form {...searchFormProps} layout="horizontal">
              <Row
                align="middle"
                justify="start"
                wrap
                className="gap-4 [&>.ant-form-item]:mb-0"
              >
                <Form.Item name="by_user_email">
                  <Input
                    placeholder="E-mail do aluno"
                    className="lg:w-[300px]"
                  />
                </Form.Item>
                <Form.Item name="by_course_name">
                  <Input placeholder="Nome do curso" className="lg:w-[300px]" />
                </Form.Item>
                <Form.Item name="by_created_at">
                  <DatePicker
                    placeholder="Data de criação"
                    className="lg:w-[300px]"
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item name="by_status">
                  <Select
                    placeholder="Status"
                    className="lg:w-[200px]"
                    options={STATUS_OPTIONS}
                    allowClear
                  />
                </Form.Item>
                <Space className="ml-auto flex-shrink-0" wrap align="center">
                  <Button
                    type="default"
                    onClick={() => {
                      searchFormProps.form?.resetFields();
                      searchFormProps.form?.submit();
                    }}
                  >
                    Limpar filtro
                  </Button>
                  <Button type="primary" htmlType="submit" ghost>
                    Filtrar
                  </Button>
                </Space>
              </Row>
            </Form>
          </Card>

          <Space className="w-full justify-end mb-6">
            <DownloadMenu
              hasFilter={Object.keys(appliedFilters).length > 0}
              onlyPending={appliedFilters.by_status === 'pending'}
              total={total}
              reportId={reportId}
              runningKind={runningKind}
              onSelect={handleGenerateCertificatesReport}
            />
          </Space>

          <Table
            {...tableProps}
            rowKey="id"
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} itens`
            }}
          >
            <Table.Column
              dataIndex="user"
              key="email"
              title="E-mail"
              render={({ email }) => <TextField value={email} />}
            />
            <Table.Column
              dataIndex="recipient_name"
              key="recipient_name"
              title="Nome"
              sorter
              render={value => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="course_name"
              key="course_name"
              title="Curso"
              sorter
              render={value => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="duration_in_hours"
              key="duration_in_hours"
              title="Duração (horas)"
              render={value => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="created_at"
              key="created_at"
              title="Data de criação"
              sorter
              render={value => (
                <TextField value={formatDate(value, 'DD/MM/YYYY')} />
              )}
            />
            <Table.Column<ICourseCertificate>
              title="Ações"
              dataIndex="actions"
              align="center"
              width={100}
              render={(_, record) => {
                const isProcessed = record.status === 'processed';

                return (
                  <Space>
                    {isProcessed ? (
                      <DownloadButton certificateId={record.id} />
                    ) : (
                      <Tooltip title="Certificado em processamento">
                        <Button
                          size="small"
                          disabled
                          icon={<Icons.HistoryOutlined />}
                        />
                      </Tooltip>
                    )}
                    <EditButton
                      hideText
                      size="small"
                      recordItemId={record.id}
                    />
                  </Space>
                );
              }}
            />
          </Table>
        </ListLayout>
      </Col>
    </Row>
  );
};
